import { Button, Spinner } from '@cimplico/components';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useEffect, useRef, useState } from 'react';
import { useCreateInternalComment } from '../../queries/use-create-internal-comment';
import { Worksheet } from '../../types/worksheet';
import { useUpdateInternalComments } from '../../queries/use-update-internal-comments';
import { InternalComment, InternalCommentOverview, UpdateInternalCommentFields } from '../../types/internal-comment';
import { toastNotification } from '../../components/toast/Toast';
import WorksheetOtherScheduleSelect from '../../components/worksheet-other-schedule-select/WorksheetOtherScheduleSelect';
import Editor, { EditorRef } from '../../components/rich-text-editor/Editor';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';

type NewOrEditInternalCommentFormProps = {
    workpaperId: string;
    defaultInternalComment?: InternalComment | InternalCommentOverview;
    isEditing?: boolean;
    onClose: () => void;
    onCreate: (id: string) => void;
};

function NewOrEditInternalCommentForm({
    workpaperId,
    defaultInternalComment,
    isEditing = false,
    onClose,
    onCreate,
}: NewOrEditInternalCommentFormProps): JSX.Element {
    const editorRef = useRef<EditorRef>(null);
    const { trackEvent } = useIntercom();
    const [worksheet, setWorksheet] = useState(defaultInternalComment?.worksheet);
    const [comment, setComment] = useState<{
        rawComment?: string;
        structuredComment?: { [key: string]: unknown } | null;
        errorMessage?: string;
    }>({
        rawComment: defaultInternalComment?.rawComment,
        structuredComment: defaultInternalComment?.structuredComment,
    });

    const updateInternalCommentsQuery = useUpdateInternalComments(workpaperId);
    const createInternalCommentQuery = useCreateInternalComment(workpaperId);

    useEffect(() => {
        setComment({
            rawComment: defaultInternalComment?.rawComment,
            structuredComment: defaultInternalComment?.structuredComment,
        });
        setWorksheet(defaultInternalComment?.worksheet);
    }, [defaultInternalComment]);

    function handleCommentChange(rawContent: string, structuredContent: string): void {
        setComment({ rawComment: rawContent, structuredComment: JSON.parse(structuredContent), errorMessage: '' });
    }

    /**
     * Sets the current context. Will be a worksheet (account worksheet or other schedule).
     * @param worksheets
     */
    function handleContextChange(worksheets: Worksheet[]): void {
        const worksheet = worksheets[0];

        if (worksheet) {
            setWorksheet({
                id: worksheet.id,
                name: worksheet.name,
                hasAccount: Boolean(worksheet.account),
                type: worksheet.type,
            });
        } else {
            setWorksheet(undefined);
        }
    }

    function handleNewCommentSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (!comment.rawComment || !comment.structuredComment) {
            setComment({
                rawComment: comment.rawComment,
                structuredComment: comment.structuredComment,
                errorMessage: 'A comment is required.',
            });
            return;
        }

        createInternalCommentQuery.mutate(
            {
                internalComments: [
                    {
                        rawComment: comment.rawComment,
                        structuredComment: comment.structuredComment,
                        worksheetId: worksheet?.id,
                    },
                ],
            },
            {
                onSuccess: (res) => {
                    if (res.results[0].data?.id) {
                        onCreate(res.results[0].data?.id);
                        trackEvent(intercomEvents.internalComment.created.event);
                        editorRef.current?.clear();
                    }
                },
            }
        );
    }

    function handleEditCommentSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (!comment.rawComment || !comment.structuredComment) {
            setComment((prev) => ({ ...prev, errorMessage: 'A comment is required.' }));
            toastNotification('error', 'Unable to edit internal comment!');
            return;
        }

        if (!defaultInternalComment) {
            return;
        }

        const fields: UpdateInternalCommentFields = {};

        if (comment.rawComment !== defaultInternalComment.rawComment) {
            fields.rawComment = comment.rawComment;
        }
        if (JSON.stringify(comment.structuredComment) !== JSON.stringify(defaultInternalComment.structuredComment)) {
            fields.structuredComment = comment.structuredComment;
        }
        if (worksheet?.id !== defaultInternalComment.worksheet?.id) {
            fields.worksheetId = worksheet?.id || null;
        }

        if (Object.keys(fields).length === 0) {
            onCreate(defaultInternalComment.id);
            return;
        }

        updateInternalCommentsQuery.mutate([{ id: defaultInternalComment.id, fields }], {
            onError: (e) => {
                const result = e.response?.data.results[0];
                setComment((prev) => ({ ...prev, errorMessage: result.error?.fields?.name }));
            },
            onSuccess: () => {
                onCreate(defaultInternalComment.id);
                toastNotification('success', 'Internal comment updated!');
            },
        });
    }

    return (
        <div
            className={`h-[725px] pl-6 pr-2 pt-2 relative ${
                createInternalCommentQuery.isLoading || updateInternalCommentsQuery.isLoading ? 'pointer-events-none' : ''
            }`}
        >
            {(createInternalCommentQuery.isLoading || updateInternalCommentsQuery.isLoading) && (
                <div className="pointer-events-none absolute top-0 left-0 w-full h-full z-50 bg-white/60">
                    <div className="absolute top-1/3 left-1/2">
                        <Spinner />
                    </div>
                </div>
            )}
            <div className="flex items-center justify-between">
                <p className="text-2xl font-medium text-gray-800">{defaultInternalComment ? 'Edit' : 'New'} Thread</p>
                <Button type="hollow" size="sm" leftIcon={<XMarkIcon />} onClick={onClose} />
            </div>

            <form className="flex flex-col gap-4 mt-8" onSubmit={isEditing ? handleEditCommentSubmit : handleNewCommentSubmit}>
                <WorksheetOtherScheduleSelect
                    isMulti={false}
                    title="Context"
                    isOptional
                    onChange={handleContextChange}
                    placeholder="Select a worksheet or schedule for this thread..."
                    selectedIds={worksheet ? [worksheet.id] : []}
                />
                <div className="h-[300px]">
                    <Editor
                        ref={editorRef}
                        placeholder="Enter a comment..."
                        defaultContent={defaultInternalComment ? JSON.stringify(defaultInternalComment.structuredComment) : undefined}
                        onContentChange={handleCommentChange}
                        errorMessage={comment.errorMessage}
                    />
                </div>
                <div className="ml-auto mr-0">
                    <Button
                        type="hollow"
                        label="Cancel"
                        size="xs"
                        onClick={onClose}
                        disabled={createInternalCommentQuery.isLoading || updateInternalCommentsQuery.isLoading}
                    />
                    <Button
                        type="primary"
                        label={isEditing ? 'Save' : 'Submit'}
                        size="xs"
                        isSubmit
                        disabled={createInternalCommentQuery.isLoading || updateInternalCommentsQuery.isLoading}
                    />
                </div>
            </form>
        </div>
    );
}
export default NewOrEditInternalCommentForm;
